import {
  Member,
  MemberRoleType,
  MemberType,
  Milestone,
  Resolution as ResolutionProto,
  SearchableJob as JobProto,
  Status,
  WaitingOn,
} from '@taxfyle/api-internal/internal/job_pb'

import { JobProgressionStepStatus as ProgressionStepStatusProto } from '@taxfyle/api-internal/internal/job_progression_pb'
import Workspace from 'iam/models/Workspace'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { getFeatureToggleClient } from 'infra/featureToggles'
import { Model } from 'infra/model'
import { isNil, once } from 'lodash'
import { PrimeConversation } from 'messaging/ConversationStore'
import Conversation from 'messaging/models/Conversation'
import { computed, makeObservable, observable } from 'mobx'
import { Id, Nullable } from 'types/definitions'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { decimalToNumber, timestampToISO } from 'utils/grpc-util'
import { generateAvatarFallback } from 'utils/image-util'
import { formatMoney } from 'utils/money-util'
import { match } from 'utils/pattern-match-util'
import { JobStatus } from './dtos/job'
import { Resolution } from './Job'
import { JobProgressionStepStatus } from './progression/JobProgression'
import { JobScopeChangeStatus } from '@taxfyle/api-internal/internal/job_scope_change_pb'
import { GetTeamMember } from 'iam/TeamMemberStore'
import { GetCurrentMember } from 'iam/SessionStore'
import { LegendVersionId } from 'legends/PublishedLegend'
import { PrimePublishedLegend } from 'legends/PublishedLegendStore'

export default class SearchableJob extends Model<
  JobProto.AsObject,
  SearchableJob
> {
  @observable resolution?: Resolution | null = null
  @observable dateDeadline: Date | null = null
  @observable dateCreated = DateZero()
  @observable dateSubmitted: Date | null = null
  @observable dateAccepted: Date | null = null
  @observable dateClosed: Date | null = null
  @observable dateModified: Date | null = null
  @observable dateLastActivity: Nullable<Date> = null
  @observable dateLastProMessageActivity: Nullable<Date> = null
  @observable dateLastClientMessageActivity: Nullable<Date> = null
  @observable dateLastRelationshipManagerMessageActivity: Nullable<Date> = null
  @observable id = ''
  @observable workspace = Workspace.none
  @observable legendName = ''
  @observable legendId = ''
  @observable legendVersion = 0
  @observable name = ''
  @observable description = '' // The computed job title.
  @observable shortId = ''
  @observable status: JobStatus = 'UNDER_CONSTRUCTION'
  @observable progressionStep: JobProgressionStepStatus =
    JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED

  @observable milestones: SearchableMilestone[] = []
  @observable members: SearchableMember[] = []
  @observable waitingOn: WaitingOn | null = null
  @observable jobScopeItems: string[] = []
  @observable clientTeamName: string | undefined = undefined
  @observable clientTeamId: string | undefined = undefined
  @observable proTeamName: string | undefined = undefined
  @observable proTeamId: string | undefined = undefined
  @observable conversation: Conversation | null = null
  @observable providerCutAmount?: number = undefined
  @observable total?: number = undefined
  @observable archived: boolean = false
  @observable isRequestedPro?: boolean = false
  @observable scopeChangeStatus: JobScopeChangeStatus =
    JobScopeChangeStatus.UNSPECIFIED

  constructor(
    private primeWorkspace: PrimeWorkspace,
    private primeConversation: PrimeConversation,
    private primePublishedLegend: PrimePublishedLegend,
    private getCurrentUserId: () => string | null,
    private getTeamMember: GetTeamMember,
    private getCurrentMember: GetCurrentMember,
  ) {
    super()
    makeObservable(this)
  }

  static get none() {
    return lazyNone()
  }

  /**
   * Display name.
   */
  @computed
  get displayName() {
    return this.name || this.description || this.legendName
  }

  /**
   * Current milestone.
   */
  @computed
  get currentMilestone(): SearchableMilestone | null {
    if (this.status === 'UNDER_CONSTRUCTION') {
      return null
    }

    return this.milestones.find((m) => !m.completed) || null
  }

  /**
   * Milestone completion percentage.
   */
  @computed
  get milestoneCompletionPercentage() {
    let tasksCount = 0
    let completedCount = 0

    this.milestones.forEach((m) => {
      completedCount += m.tasksList.filter((t) => t.completed).length
      tasksCount += m.tasksList.length
    })

    return Math.round((completedCount / Math.max(tasksCount, 1)) * 100)
  }

  /**
   * If job is on hold.
   */
  @computed
  get isOnHold() {
    return this.status === 'ON_HOLD'
  }

  /**
   * If the job is actively being worked on.
   */
  @computed
  get isInProgress() {
    return (
      this.members.find(
        (member) => member.type === 'PROVIDER' && member.role === 'CHAMPION',
      ) &&
      this.status !== 'CLOSED' &&
      this.status !== 'UNDER_CONSTRUCTION' &&
      this.status !== 'ON_HOLD'
    )
  }

  /**
   * Client members on the job.
   */
  @computed
  get clientMembers() {
    return this.members.filter((c) => c.type === 'CLIENT').map((c) => c)
  }

  /**
   * Pro members on the job.
   */
  @computed
  get proMembers() {
    return this.members.filter((c) => c.type === 'PROVIDER').map((c) => c)
  }

  /**
   * The champion client.
   */
  @computed
  get championClient() {
    return this.members.find(
      (c) => c.type === 'CLIENT' && c.role === 'CHAMPION',
    )
  }

  /**
   * The champion provider.
   */
  @computed
  get championPro() {
    return this.members.find(
      (c) => c.type === 'PROVIDER' && c.role === 'CHAMPION',
    )
  }

  /**
   * The current user on the job.
   */
  @computed
  get currentUserMember() {
    return this.members.find((m) => m.isCurrentUser) || null
  }

  /**
   * The champion of the opposite type than the current user.
   */
  @computed
  get otherChampion() {
    return this.members.find(
      (c) => c.role === 'CHAMPION' && c.type !== this.currentUserMember?.type,
    )
  }

  /**
   * Secondary info.
   */
  @computed
  get secondaryInfo() {
    const otherChampionText = this.otherChampion
      ? this.otherChampion.type === 'PROVIDER'
        ? `Assigned to ${this.otherChampion.displayName}`
        : this.otherChampion.displayName
      : null

    return [otherChampionText, this.jobScopeItems.join(' • ')]
      .filter(Boolean)
      .join(' | ')
  }

  /**
   * The provider cut formated in USD.
   */
  @computed
  get formattedProviderCutAmount() {
    if (!this.providerCutAmount) {
      return ''
    }
    return formatMoney(this.providerCutAmount)
  }

  /**
   * The total price formatted in USD.
   */
  get formattedTotalPrice() {
    if (!this.total) {
      return ''
    }
    return formatMoney(this.total)
  }

  /**
   * Whether the new job progression config is being used.
   */
  @computed
  get usingNewJobProgressionConfig() {
    return getFeatureToggleClient().variation(
      'HQ.UseUpdatedJobProgressionConfig',
    )
  }

  /**
   * Whether the job progression flow is enabled.
   */
  @computed
  get isProgressionFlowEnabled() {
    return this.usingNewJobProgressionConfig
      ? this.workspace.features.jobProgressionConfig.enabled
      : this.workspace.features.jobConfig.jobProgressionConfig
  }

  /*
   * Whether is a draft job.
   */
  @computed
  get isDraft() {
    return this.status === 'UNDER_CONSTRUCTION'
  }

  @computed
  get canCopy() {
    return (
      (!this.isDraft &&
        this.getCurrentMember(this.workspace.id).hasSomePermissions(
          'ADMIN_JOB_CREATE',
          'JOB_CREATE',
        )) ||
      this.getTeamMember(
        this.clientTeamId,
        this.getCurrentMember().user.publicId,
      )?.hasSomePermissions('CREATE_TEAM_JOBS')
    )
  }

  /**
   * Whether we are able to make any transfer in a job.
   */
  @computed
  get canTransferJobAsAdmin() {
    return (
      this.status !== 'CLOSED' &&
      this.getCurrentMember(this.workspace.id).hasAllPermissions(
        'ADMIN_JOB_TRANSFER',
        'ADMIN_USER_LIST',
        'ADMIN_USER_READ',
        'ADMIN_JOB_VIEW',
      )
    )
  }

  @computed
  get shouldShowJobTotal() {
    const currentMember = this.getCurrentMember(this.workspace.id)

    const isClientCollaborator = this.clientMembers.some(
      (member) => member.isCurrentUser,
    )

    const canViewPricing = currentMember.hasPermission('VIEW_PRICING')

    const onOwnerTeamWithViewPricing = Boolean(
      this.clientTeamId &&
        this.getTeamMember(
          this.clientTeamId,
          currentMember.user.publicId,
        )?.hasPermission('VIEW_PRICING'),
    )

    const canViewPricingAsAdmin =
      currentMember.hasPermission('ADMIN_VIEW_PRICING')

    return (
      (isClientCollaborator && canViewPricing) ||
      onOwnerTeamWithViewPricing ||
      canViewPricingAsAdmin
    )
  }

  parse(json: Partial<JobProto.AsObject>): Partial<SearchableJob> {
    if (json.legendId && json.legendVersion) {
      const legendVersionId = LegendVersionId(json.legendId, json.legendVersion)
      this.primePublishedLegend(legendVersionId)
    }

    return {
      legendName: json.legendName,
      legendId: json.legendId,
      legendVersion: json.legendVersion,
      dateDeadline: maybeParseDate(timestampToISO(json.dateDeadline)),
      dateCreated: maybeParseDate(timestampToISO(json.dateCreated)),
      resolution: !isNil(json.resolution)
        ? this.toResolution(json.resolution)
        : json.resolution,
      dateSubmitted: maybeParseDate(timestampToISO(json.dateSubmitted)),
      dateAccepted: maybeParseDate(timestampToISO(json.dateClaimed)),
      dateClosed: maybeParseDate(timestampToISO(json.dateCompleted)),
      dateModified: maybeParseDate(timestampToISO(json.dateModified)),
      dateLastProMessageActivity: maybeParseDate(
        timestampToISO(json.dateLastProMessageActivity),
      ),
      dateLastClientMessageActivity: maybeParseDate(
        timestampToISO(json.dateLastClientMessageActivity),
      ),
      dateLastRelationshipManagerMessageActivity: maybeParseDate(
        timestampToISO(json.dateLastRelationshipManagerMessageActivity),
      ),
      dateLastActivity: maybeParseDate(timestampToISO(json.dateLastActivity)),
      id: json.id,
      workspace: json.workspaceId
        ? this.primeWorkspace({ id: json.workspaceId })
        : undefined,
      conversation: json.conversationId
        ? this.primeConversation({ id: json.conversationId.value })
        : null,
      shortId: json.shortId,
      name: json.title,
      description: json.description,
      status: json.status
        ? match(json.status, {
            [Status.UNDER_CONSTRUCTION]: () => 'UNDER_CONSTRUCTION',
            [Status.INFO_GATHERING]: () => 'INFO_GATHERING',
            [Status.UNCLAIMED]: () => 'UNCLAIMED',
            [Status.CLAIMED]: () => 'CLAIMED',
            [Status.CLOSED]: () => 'CLOSED',
            [Status.IDLE]: () => 'IDLE',
            [Status.ON_HOLD]: () => 'ON_HOLD',
          })
        : undefined,
      progressionStep: json.progressionStep
        ? match<ProgressionStepStatusProto, JobProgressionStepStatus>(
            json.progressionStep,
            {
              [ProgressionStepStatusProto.PROGRESSION_STEP_UNSPECIFIED]: () =>
                JobProgressionStepStatus.PROGRESSION_STEP_UNSPECIFIED,
              [ProgressionStepStatusProto.JOB_STARTED]: () =>
                JobProgressionStepStatus.JOB_STARTED,
              [ProgressionStepStatusProto.OPEN_ITEMS]: () =>
                JobProgressionStepStatus.OPEN_ITEMS,
              [ProgressionStepStatusProto.DRAFT_IN_REVIEW]: () =>
                JobProgressionStepStatus.DRAFT_IN_REVIEW,
              [ProgressionStepStatusProto.DRAFT_APPROVED]: () =>
                JobProgressionStepStatus.DRAFT_APPROVED,
              [ProgressionStepStatusProto.DRAFT_REJECTED]: () =>
                JobProgressionStepStatus.DRAFT_REJECTED,
              [ProgressionStepStatusProto.AUTHORIZATION_REQUESTED]: () =>
                JobProgressionStepStatus.AUTHORIZATION_REQUESTED,
              [ProgressionStepStatusProto.AUTHORIZATION_SIGNED]: () =>
                JobProgressionStepStatus.AUTHORIZATION_SIGNED,
              [ProgressionStepStatusProto.RETURN_FILED]: () =>
                JobProgressionStepStatus.RETURN_FILED,
              [ProgressionStepStatusProto.NOT_STARTED]: () =>
                JobProgressionStepStatus.NOT_STARTED,
              [ProgressionStepStatusProto.DELIVERABLE_COMPLETED]: () =>
                JobProgressionStepStatus.DELIVERABLE_COMPLETED,
              [ProgressionStepStatusProto.PREPARATION_COMPLETED]: () =>
                JobProgressionStepStatus.PREPARATION_COMPLETED,
              [ProgressionStepStatusProto.REVIEW_COMPLETED]: () =>
                JobProgressionStepStatus.REVIEW_COMPLETED,
              [ProgressionStepStatusProto.EXTENSION_IN_PROGRESS]: () =>
                JobProgressionStepStatus.EXTENSION_IN_PROGRESS,
              [ProgressionStepStatusProto.EXTENSION_CONFIRMED]: () =>
                JobProgressionStepStatus.EXTENSION_CONFIRMED,
              [ProgressionStepStatusProto.EXTENSION_ACCEPTED]: () =>
                JobProgressionStepStatus.EXTENSION_ACCEPTED,
              [ProgressionStepStatusProto.EXTENSION_REJECTED]: () =>
                JobProgressionStepStatus.EXTENSION_REJECTED,
              [ProgressionStepStatusProto.PROGRESSION_WAITING_ON_CLIENT]: () =>
                JobProgressionStepStatus.PROGRESSION_WAITING_ON_CLIENT,
              [ProgressionStepStatusProto.READY_TO_FILE]: () =>
                JobProgressionStepStatus.READY_TO_FILE,
            },
          )
        : undefined,
      milestones: json.milestonesList?.map((m) => this.toMilestone(m)),
      waitingOn: json.waitingOn,
      members: json.membersList?.map((m) => this.toSearchableMember(m)),
      scopeChangeStatus: json.scopeChangeStatus,
      jobScopeItems: json.jobScopeItemsList,
      clientTeamName: json.clientTeamName,
      clientTeamId: json.clientTeamId,
      proTeamName: json.proTeamName,
      proTeamId: json.proTeamId,
      providerCutAmount: json.providerCutAmount
        ? decimalToNumber(json.providerCutAmount)
        : undefined,
      total: json.total ? decimalToNumber(json.total) : undefined,
      archived: json.archived,
      isRequestedPro: json.isRequestedPro?.value,
    }
  }

  toResolution(resolution: ResolutionProto) {
    switch (resolution) {
      case ResolutionProto.DONE:
        return Resolution.Done
      case ResolutionProto.CANCELLED:
        return Resolution.Cancelled
      case ResolutionProto.TEST:
        return Resolution.Test
      case ResolutionProto.RESOLUTION_UNSPECIFIED:
        return null
      default:
        throw new Error(`Unknown resolution: ${resolution}`)
    }
  }

  /**
   * Maps a proto `Member` to a `SearchableMember`
   *
   * @param member
   * @returns
   */
  toSearchableMember(member: Member.AsObject): SearchableMember {
    return {
      id: member.id,
      displayName: member.displayName,
      email: member.email?.value,
      avatar: member.picture,
      isCurrentUser:
        Id.some(member.id) && member.id === this.getCurrentUserId(),
      type: match(member.type, {
        [MemberType.MEMBER_TYPE_UNSPECIFIED]: () => 'UNSPECIFIED',
        [MemberType.MEMBER_TYPE_PROVIDER]: () => 'PROVIDER',
        [MemberType.MEMBER_TYPE_CLIENT]: () => 'CLIENT',
      }),
      role: match(member.role, {
        [MemberRoleType.MEMBER_ROLE_TYPE_UNSPECIFIED]: () => 'UNSPECIFIED',
        [MemberRoleType.SUPPORTING]: () => 'SUPPORTING',
        [MemberRoleType.CHAMPION]: () => 'CHAMPION',
      }),
      avatarFallback: generateAvatarFallback(
        member.displayName,
        this.primeWorkspace({ id: this.workspace.id })?.branding.colors.primary,
      ),
    }
  }

  toMilestone(milestone: Milestone.AsObject): SearchableMilestone {
    return {
      id: milestone.id,
      title: milestone.title,
      completed: milestone.completed,
      tasksList: milestone.tasksList.map((t) => {
        return {
          id: t.id,
          completed: t.completed,
          dateCompleted:
            maybeParseDate(timestampToISO(t.dateCompleted)) || DateZero(),
        }
      }),
    }
  }
}

const lazyNone = once(() => undefined)

/**
 * Member.
 */
export interface SearchableMember {
  readonly id: string
  readonly teamId?: string
  readonly displayName: string
  readonly avatar: string
  readonly avatarFallback: string | null
  readonly type: SearchableMemberType
  readonly role: SearchableMemberRoleType
  readonly isCurrentUser: boolean
  readonly email?: string | null
}

/**
 * Member type.
 */
export type SearchableMemberType = 'UNSPECIFIED' | 'PROVIDER' | 'CLIENT'

/**
 * Member role type.
 */
export type SearchableMemberRoleType = 'UNSPECIFIED' | 'SUPPORTING' | 'CHAMPION'

/**
 * Milestone.
 */
export interface SearchableMilestone {
  readonly id: string
  readonly title: string
  readonly completed: boolean
  readonly tasksList: SearchableMilestoneTask[]
}

/**
 * Milestone task.
 */
export interface SearchableMilestoneTask {
  id: string
  completed: boolean
  dateCompleted: Date
}
